import { forwardRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDemo } from '@/contexts/DemoContext';
import { DemoConversionCta } from './DemoConversionCta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { calculateDaysRemaining } from '@/lib/utils';

export const DemoProfileSection = forwardRef<HTMLElement>(
  function DemoProfileSection(_, ref) {
    const { t, language } = useLanguage();
    const { mockProfile, mockGroup, demoTotalDrinks, demoCurrentStreak, demoLongestStreak, demoBeerCount, demoWineCount, demoShotCount } = useDemo();

    const daysRemaining = calculateDaysRemaining(mockGroup.challenge_end);
    const remainingDrinks = Math.max(0, 100 - demoTotalDrinks);
    const weekendsRemaining = Math.ceil(daysRemaining / 7);
    const drinksPerWeekend = weekendsRemaining > 0 ? (remainingDrinks / weekendsRemaining).toFixed(1) : '0';

    // Mock last drink: 2 days ago
    const lastDrinkDate = new Date();
    lastDrinkDate.setDate(lastDrinkDate.getDate() - 2);
    lastDrinkDate.setHours(21, 43, 0, 0);

    const currentMonthName = new Date().toLocaleDateString(language === 'sk' ? 'sk-SK' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });

    return (
      <section ref={ref} id="profile" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold">
            {mockProfile.user_id}
          </h2>
        </div>

        <div className="px-6 max-w-lg mx-auto space-y-6">
          {/* Challenge Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('profileChallengeProgress')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="text-3xl font-bold text-primary">{demoTotalDrinks}</div>
                  <div className="text-sm text-muted-foreground">{t('profileDrinksLogged')}</div>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="text-3xl font-bold">{remainingDrinks}</div>
                  <div className="text-sm text-muted-foreground">{t('profileRemaining')}</div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="p-2">
                  <div className="text-2xl">🍺</div>
                  <div className="font-bold">{demoBeerCount}</div>
                </div>
                <div className="p-2">
                  <div className="text-2xl">🍷</div>
                  <div className="font-bold">{demoWineCount}</div>
                </div>
                <div className="p-2">
                  <div className="text-2xl">🥃</div>
                  <div className="font-bold">{demoShotCount}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('profilePersonalStats')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {daysRemaining} {t('profileDaysRemaining')}
              </p>
              <div className="text-sm">
                <span className="text-muted-foreground">{t('profileLastDrink')} </span>
                <span className="font-medium">
                  {lastDrinkDate.toLocaleString(language === 'sk' ? 'sk-SK' : 'en-GB', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }).replace(',', ' at')}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">{t('profileCurrentStreak')} </span>
                <span className="font-medium">{demoCurrentStreak} {t('profileDaysWithoutAlcohol')}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">{t('profileLongestStreak')} </span>
                <span className="font-medium">{demoLongestStreak} {t('profileDaysWithoutAlcohol')}</span>
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  {t('profileWeekendStats')} <strong>{weekendsRemaining} {t('profileWeekends')}</strong>, {t('profileYouCanDrink')}{' '}
                  <strong>{remainingDrinks} {t('profileDrinks')}</strong> {t('profileThatsAbout')}{' '}
                  <strong>{drinksPerWeekend} {t('profileDrinksPerWeekend')}</strong>.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('profileMonthlyOverview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {/* Header */}
                <div className="grid grid-cols-[1fr_repeat(4,_40px)] gap-2 text-xs text-muted-foreground font-medium pb-2 border-b border-border">
                  <span>{t('profileMonth')}</span>
                  <span className="text-center">🍺</span>
                  <span className="text-center">🍷</span>
                  <span className="text-center">🥃</span>
                  <span className="text-center">Σ</span>
                </div>

                {/* Current month row */}
                <div className="grid grid-cols-[1fr_repeat(4,_40px)] gap-2 py-2">
                  <span className="text-sm">{currentMonthName}</span>
                  <span className="text-center text-sm">{demoBeerCount}</span>
                  <span className="text-center text-sm">{demoWineCount}</span>
                  <span className="text-center text-sm">{demoShotCount}</span>
                  <span className="text-center text-sm font-bold">{demoTotalDrinks}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversion CTA */}
          <DemoConversionCta variant="inline" className="mt-2" />
        </div>
      </section>
    );
  }
);
